// Beta-0.5C · Settings Route Registry — SINGLE SOURCE OF TRUTH
//
// This registry drives:
//   • the sidebar (SettingsLayout.js)
//   • settings search (SettingsSearch.js)
//   • breadcrumbs
//   • label-only pages (書類とナレッジ placeholders)
//   • redirect matrix (see /app/memory/BETA_0_5C_REDIRECT_MATRIX.md)
//   • documentation
//
// Rules (locked by Beta-0.5C spec):
//   - Existing routes are UNCHANGED. Only groupings and labels change.
//   - No page moves URL except AI Code Interpreter, which now lives at
//     /settings/ai-code (UX consolidation 2026-06); /ai/code redirects there.
//   - Do not rename API routes, DB collections, or backend services.
//   - Do not add "AI管理コンソール → AI機能ごとのモデル設定" rename; the
//     console is broader than routing. Use「AI管理」for the overall area.
//
// Entry shape:
//   {
//     path: string,                // canonical settings URL
//     labelJa: string,             // plain-Japanese sidebar label
//     aliasesJa: string[],         // additional search aliases (JA + EN)
//     group: string,               // one of the 9 group ids below
//     purpose: string,             // one-line plain-Japanese purpose
//     capability: keyof CAPS,      // gate — must match a name in CAPS below
//     tags: string[],              // free-form searchable tags
//     deprecated?: boolean,        // hidden from sidebar, still routable
//     redirectTo?: string,         // internal target for deprecated
//     external?: boolean,          // sidebar entry links out of /settings
//     iconKey?: string,            // logical icon name (SettingsLayout maps)
//   }
//
// Capability names below are strings; SettingsLayout resolves them to real
// predicates from AuthContext. Keeping them as strings keeps the registry
// framework-free and easy to unit-test.

export const GROUP_ORDER = [
  'personal',  // 環境設定（個人）
  'org',       // 組織と権限
  'master',    // マスター
  'workflow',  // 業務ルール
  'notify',    // 通知とメール
  'ai',        // AIと検索
  'docs',      // 書類とナレッジ  (Beta-0.5C: labels + placeholders only)
  'storage',   // 保存・NAS・バックアップ
  'audit',     // 監査と履歴
  'system',    // システム管理
];

export const GROUPS = {
  personal: { id: 'personal', labelJa: '環境設定',              iconKey: 'ClockClockwise' },
  org:      { id: 'org',      labelJa: '組織と権限',          iconKey: 'UsersThree' },
  master:   { id: 'master',   labelJa: 'マスター',            iconKey: 'BookmarkSimple' },
  workflow: { id: 'workflow', labelJa: '業務ルール',          iconKey: 'GitBranch' },
  notify:   { id: 'notify',   labelJa: '通知とメール',        iconKey: 'BellRinging' },
  ai:       { id: 'ai',       labelJa: 'AIと検索',            iconKey: 'Brain' },
  docs:     { id: 'docs',     labelJa: '書類とナレッジ',      iconKey: 'FolderSimple' },
  storage:  { id: 'storage',  labelJa: '保存・NAS・バックアップ', iconKey: 'HardDrives' },
  audit:    { id: 'audit',    labelJa: '監査と履歴',          iconKey: 'ClockCounterClockwise' },
  system:   { id: 'system',   labelJa: 'システム管理',        iconKey: 'Gear' },
};

// Capability tokens — resolved to predicates in SettingsLayout.
export const CAP = {
  ALWAYS: 'ALWAYS',
  IS_ADMIN: 'isAdmin',
  IS_MANAGER: 'isManager',
  MANAGE_EMPLOYEES: 'canManageEmployees',
  MANAGE_USERS: 'canManageUsers',
  MANAGE_EMAIL: 'canManageEmail',
  VIEW_AUDIT_LOG: 'canViewAuditLog',
  MANAGE_LABELS: 'canManageLabels',
  BACKUP: 'canBackup',
  ADVANCED: 'canAdvancedSettings',
  MANAGE_SKU: 'canManageSku',
  AI_IMPORT_HISTORY: 'canAiImportHistory',
};

export const SETTINGS_REGISTRY = [
  // ── 環境設定（個人） ──────────────────────────────────────────────────
  { path: '/settings/preferences', group: 'personal', labelJa: 'タイムゾーン', capability: CAP.ALWAYS,
    purpose: '日時表示のタイムゾーン（既定: 東京 JST）',
    aliasesJa: ['Timezone', 'タイムゾーン', '時差', '環境設定', 'Preferences', '個人設定', '時刻'],
    tags: ['個人', '表示', '時刻', 'タイムゾーン'], iconKey: 'ClockClockwise' },

  // ── 組織と権限 ────────────────────────────────────────────────────────
  { path: '/settings/employees',   group: 'org', labelJa: '社員マスター', capability: CAP.MANAGE_EMPLOYEES,
    purpose: '社員台帳・所属・在籍状況の管理',
    aliasesJa: ['社員', '従業員', 'Employees', 'Staff'], tags: ['組織', 'HR'], iconKey: 'Users' },
  { path: '/settings/departments', group: 'org', labelJa: '部署', capability: CAP.ALWAYS,
    purpose: '部署の作成・階層・並び順',
    aliasesJa: ['Departments', '部門'], tags: ['組織'], iconKey: 'Buildings' },
  { path: '/settings/users',       group: 'org', labelJa: 'ユーザー管理', capability: CAP.MANAGE_USERS,
    purpose: 'ログインアカウントとパスワード',
    aliasesJa: ['Users', 'Login', 'アカウント'], tags: ['組織', 'アカウント'], iconKey: 'UserCircle' },
  { path: '/settings/roles',       group: 'org', labelJa: '権限管理', capability: CAP.IS_ADMIN,
    purpose: '役割ごとに使える機能を決める',
    aliasesJa: ['Roles', 'Permissions', 'ロール'], tags: ['組織', '権限'], iconKey: 'Shield' },
  { path: '/settings/eng-members', group: 'org', labelJa: '技術メンバー', capability: CAP.IS_ADMIN,
    purpose: '技術部門メンバー登録',
    aliasesJa: ['Engineering Members'], tags: ['組織', '技術'], iconKey: 'Wrench' },
  { path: '/settings/it-members',  group: 'org', labelJa: 'ITメンバー', capability: CAP.IS_ADMIN,
    purpose: 'IT部門メンバー登録',
    aliasesJa: ['IT Members'], tags: ['組織', 'IT'], iconKey: 'ShareNetwork' },
  { path: '/settings/modules',     group: 'org', labelJa: 'モジュール設定', capability: CAP.IS_ADMIN,
    purpose: '各アプリの利用可否・一時停止',
    aliasesJa: ['Modules', 'Apps', 'アプリ'], tags: ['組織', 'モジュール'], iconKey: 'PauseCircle' },

  // ── マスター ─────────────────────────────────────────────────────────
  { path: '/settings/lookups',           group: 'master', labelJa: '共通マスター', capability: CAP.ADVANCED,
    purpose: 'ドロップダウン用の共通マスターデータ',
    aliasesJa: ['Lookups', 'Common Master'], tags: ['マスター'], iconKey: 'ArrowsMerge' },
  { path: '/settings/sku-master',        group: 'master', labelJa: '品番と採番', capability: CAP.MANAGE_SKU,
    purpose: '品番ルールと採番テンプレート',
    aliasesJa: ['SKU', 'Item Numbering', 'Part Number'], tags: ['マスター', '品番'], iconKey: 'Hash' },
  { path: '/settings/mfg-master',        group: 'master', labelJa: '製造・資材マスター', capability: CAP.ADVANCED,
    purpose: '仕入先・場所・カテゴリ・単位・メーカーの共有マスター',
    aliasesJa: ['MFG Master', 'Suppliers', 'UoM', 'Category'], tags: ['マスター', '製造', '資材'], iconKey: 'Database' },
  { path: '/settings/order-categories',  group: 'master', labelJa: '発注分類', capability: CAP.ADVANCED,
    purpose: '発注書の分類とルート',
    aliasesJa: ['Order Categories'], tags: ['マスター', '資材'], iconKey: 'TreeStructure' },
  { path: '/settings/delivery-destinations', group: 'master', labelJa: '納入先', capability: CAP.ADVANCED,
    purpose: '納入先の登録',
    aliasesJa: ['Delivery Destinations'], tags: ['マスター', '資材'], iconKey: 'MapPin' },
  { path: '/settings/it-categories',     group: 'master', labelJa: 'IT分類', capability: CAP.IS_ADMIN,
    purpose: 'IT資産・チケット分類',
    aliasesJa: ['IT Categories'], tags: ['マスター', 'IT'], iconKey: 'Desktop' },
  { path: '/settings/locations',         group: 'master', labelJa: '場所', capability: CAP.IS_ADMIN,
    purpose: '全社でひとつの場所マスタ（建物 › 部屋 › 棚）',
    aliasesJa: ['Locations', '設置場所', '保管場所', '棚', 'ロケーション', '技術ロケーション'],
    tags: ['マスター', '場所'], iconKey: 'MapPin' },
  { path: '/settings/taxonomy',          group: 'master', labelJa: 'カテゴリ・品番', capability: CAP.IS_ADMIN,
    purpose: '資産カテゴリと品番グループ',
    aliasesJa: ['Taxonomy', 'Categories', '資産カテゴリ', '品番グループ'],
    tags: ['マスター', '品番', 'カテゴリ'], iconKey: 'Tag' },
  { path: '/settings/eng-shelves',       group: 'master', labelJa: '技術ロケーション', capability: CAP.IS_ADMIN,
    purpose: '技術部門の棚・保管場所',
    aliasesJa: ['Engineering Shelves'], tags: ['マスター', '技術'], iconKey: 'MapPin',
    deprecated: true, redirectTo: '/settings/locations' },
  { path: '/settings/labels',            group: 'master', labelJa: 'ラベル', capability: CAP.MANAGE_LABELS,
    purpose: 'ラベル・タグの管理と印刷',
    aliasesJa: ['Labels', 'Tags'], tags: ['マスター'], iconKey: 'Tag' },
  { path: '/settings/holidays',          group: 'master', labelJa: '祝日・休業日', capability: CAP.ADVANCED,
    purpose: '祝日・全社休業日の登録',
    aliasesJa: ['Holidays', 'Business Days'], tags: ['マスター'], iconKey: 'CalendarBlank' },

  // ── 業務ルール ───────────────────────────────────────────────────────
  { path: '/settings/workflow',           group: 'workflow', labelJa: 'ワークフロー', capability: CAP.ADVANCED,
    purpose: '申請と承認の共通ルール',
    aliasesJa: ['Workflow'], tags: ['業務ルール'], iconKey: 'FlowArrow' },
  { path: '/settings/shizai-approvals',   group: 'workflow', labelJa: '発注承認', capability: CAP.ADVANCED,
    purpose: '資材発注の承認しきい値とルール',
    aliasesJa: ['Approvals', 'Purchase Approvals'], tags: ['業務ルール', '資材'], iconKey: 'ShieldCheck', pausedModule: 'shizai' },
  { path: '/settings/shizai-delegations', group: 'workflow', labelJa: '承認の委任', capability: CAP.ALWAYS,
    purpose: '不在時に承認を他の人へ委任',
    aliasesJa: ['Delegation'], tags: ['業務ルール', '資材'], iconKey: 'UserSwitch', pausedModule: 'shizai' },
  { path: '/settings/signature',          group: 'workflow', labelJa: '印影・署名', capability: CAP.ALWAYS,
    purpose: '個人の印影と署名画像',
    aliasesJa: ['Signature'], tags: ['業務ルール'], iconKey: 'Signature' },

  // ── 通知とメール ─────────────────────────────────────────────────────
  { path: '/settings/email',                group: 'notify', labelJa: 'メール送信', capability: CAP.MANAGE_EMAIL,
    purpose: 'SMTP と送信元アドレスの設定',
    aliasesJa: ['Email', 'SMTP'], tags: ['通知', 'メール'], iconKey: 'EnvelopeSimple' },
  { path: '/settings/email-templates',      group: 'notify', labelJa: 'メールテンプレート', capability: CAP.MANAGE_EMAIL,
    purpose: '定型メールのひな型',
    aliasesJa: ['Email Templates'], tags: ['通知', 'メール'], iconKey: 'EnvelopeOpen' },
  { path: '/settings/shizai-notifications', group: 'notify', labelJa: '資材の通知', capability: CAP.ADVANCED,
    purpose: '資材モジュールのイベント通知先',
    aliasesJa: ['Shizai Notifications'], tags: ['通知', '資材'], iconKey: 'BellRinging', pausedModule: 'shizai' },
  { path: '/settings/admin-notify',         group: 'notify', labelJa: 'システム通知', capability: CAP.ADVANCED,
    purpose: '管理者向けの通知チャネル',
    aliasesJa: ['System Notifications'], tags: ['通知', 'システム'], iconKey: 'BellRinging' },

  // ── AIと検索 ─────────────────────────────────────────────────────────
  // ("AI管理" は AI管理コンソール の総称。ルーティング設定はその中の1タブ)
  { path: '/settings/ai-preferences',   group: 'ai', labelJa: 'AIの個人設定', capability: CAP.ALWAYS,
    purpose: '自分だけのAI応答スタイル・記憶',
    aliasesJa: ['AI Preferences', 'Personal'], tags: ['AI', '個人'], iconKey: 'Sparkle' },
  { path: '/settings/ai-console',       group: 'ai', labelJa: 'AI管理', capability: CAP.IS_ADMIN,
    purpose: 'AI機能ごとのモデル設定・プロンプト・使用状況',
    aliasesJa: ['AI Console', 'AI Management', '機能ルーティング', 'モデル設定',
      'プロンプト', 'RAG', 'Embeddings', '意味検索', '文書生成',
      'AI利用状況', 'AI診断', 'AI Diagnostics'],
    tags: ['AI', 'モデル', 'プロンプト', '検索'], iconKey: 'Cpu' },
  { path: '/settings/ai-learning',      group: 'ai', labelJa: 'AI学習対象', capability: CAP.IS_ADMIN,
    purpose: 'AI学習に使うデータソース（NAS・社内ナレッジ）',
    aliasesJa: ['AI Learning Sources', 'AI学習ソース', 'Crawler', 'NAS Crawl',
      'Knowledge Source', '学習ソース', '索引'],
    tags: ['AI', 'NAS', '検索', '索引', 'Crawler'], iconKey: 'Brain' },
  { path: '/settings/ai-search-status', group: 'ai', labelJa: 'AIが検索できる情報', capability: CAP.IS_ADMIN,
    purpose: 'AIが現在どれだけの社内情報を検索できるかの一覧',
    aliasesJa: ['AI Search Status', 'Searchable Info', 'coverage', '検索状況'],
    tags: ['AI', '検索', '索引', 'ステータス'], iconKey: 'MagnifyingGlass' },

  // ── 書類とナレッジ (Beta-0.5D-A: profile UI + terminology live; material/retention Beta-0.5D-B) ─
  { path: '/settings/ai-learning',      group: 'docs', labelJa: 'NASの検索対象と機密除外',
    capability: CAP.IS_ADMIN,
    purpose: 'NASのどこをAIが検索できるか（除外キーワード・保護フォルダー）',
    aliasesJa: ['機密フォルダーガード', 'NAS Guard', 'Confidential Folder Guard',
      'Evidence Router Privacy', 'Privacy'],
    tags: ['書類', 'NAS', 'プライバシー', '機密'],
    iconKey: 'Shield', synonymOf: '/settings/ai-learning' },
  { path: '/settings/traceability',     group: 'docs', labelJa: '関連情報とトレーサビリティ',
    capability: CAP.IS_ADMIN,
    purpose: '書類・現物・帳票の関連付けの追跡',
    aliasesJa: ['Traceability', 'Related Documents'],
    tags: ['書類', 'ナレッジ', '関連'], iconKey: 'TreeStructure' },
  { path: '/settings/doc-profiles', group: 'docs', labelJa: '書類の種類と読み取り設定',
    capability: CAP.IS_ADMIN,
    purpose: '書類プロファイル（Beta-1）のしきい値・必須項目・健全性',
    aliasesJa: ['Document Profile', 'Doc Profile', 'Beta-1 プロファイル',
      'プロファイル', 'しきい値', 'confidence'],
    tags: ['書類', 'プロファイル', 'Beta-1'], iconKey: 'Sparkle' },
  { path: '/settings/doc-learning', group: 'docs', labelJa: '書類読み取り学習',
    capability: CAP.IS_ADMIN,
    purpose: '例として登録した書類と人の修正から、承認された読み取り知識を育てる',
    aliasesJa: ['Document Intelligence Learning', '書類学習', '読み取り学習',
      'OCR学習', '図面学習', '押印学習', '学習用書類', 'training',
      'document learning', 'stamp learning', 'drawing learning',
      '学習候補', '承認待ち', '学習履歴', '効果の確認', '元に戻す',
      '図面レイアウト', '押印', 'ハンコ', '使い方'],
    tags: ['書類', '学習', 'OCR', '押印', '図面'], iconKey: 'Sparkle' },
  { path: '/settings/terminology', group: 'docs', labelJa: '社内用語',
    capability: CAP.IS_ADMIN,
    purpose: '会社辞書・AI用語集・社内用語辞書の統合ビュー（衝突検出付き）',
    aliasesJa: ['Terminology', 'Glossary', '用語集', '辞書', 'company_dictionary'],
    tags: ['用語', '辞書', 'ナレッジ'], iconKey: 'BookmarkSimple' },
  { path: '/settings/materials', group: 'docs', labelJa: '材質辞書',
    capability: CAP.IS_ADMIN,
    purpose: '材質コード・エイリアス・OCR揺れの登録と健全性',
    aliasesJa: ['Material Dictionary', '材質', 'OCR候補', 'material'],
    tags: ['書類', '材質', 'OCR'], iconKey: 'Database' },
  { path: '/settings/materials/unknown', group: 'docs', labelJa: '未登録材質のキュー',
    capability: CAP.IS_ADMIN,
    purpose: 'OCRで検出された未登録材質を既存材質へ紐付ける',
    aliasesJa: ['Unknown Material', '未登録', 'Queue'],
    tags: ['書類', '材質', 'OCR', 'キュー'], iconKey: 'ClockCounterClockwise' },

  // ── 保存・NAS・バックアップ ─────────────────────────────────────────
  { path: '/settings/storage',   group: 'storage', labelJa: '保存先', capability: CAP.IS_ADMIN,
    purpose: 'ファイル本体を保存するNAS・オブジェクトストレージ',
    aliasesJa: ['Storage', 'File Storage', '保存先NAS'],
    tags: ['保存', 'NAS', 'Storage'], iconKey: 'HardDrives' },
  { path: '/settings/ai-learning', group: 'storage', labelJa: 'NAS Agent v1.3', capability: CAP.IS_ADMIN,
    purpose: 'NASのブラウジング・クロール接続とネットワーク診断',
    aliasesJa: ['NAS Agent', 'NASクロール接続', 'Crawler'],
    tags: ['NAS', 'Agent', 'クロール'], iconKey: 'ShareNetwork', synonymOf: '/settings/ai-learning' },
  { path: '/settings/backup',    group: 'storage', labelJa: 'バックアップ', capability: CAP.BACKUP,
    purpose: '定期バックアップと復元',
    aliasesJa: ['Backup', 'Restore'],
    tags: ['保存', 'バックアップ'], iconKey: 'Database' },
  { path: '/settings/retention', group: 'storage', labelJa: '保持期間', capability: CAP.IS_ADMIN,
    purpose: 'DMS ゴミ箱・受領写真・バックアップ・監査ログ・検索索引の保持ルール一覧',
    aliasesJa: ['Retention', '保持', 'Purge', '削除ルール'],
    tags: ['保存', '保持', '監査', 'NAS'], iconKey: 'ClockCounterClockwise' },

  // ── 監査と履歴 ────────────────────────────────────────────────────────
  // ログは記録であり、設定ではない。ここへ集約する。
  { path: '/settings/audit-log',        group: 'audit', labelJa: '監査ログ', capability: CAP.VIEW_AUDIT_LOG,
    purpose: '設定変更・重要操作の履歴',
    aliasesJa: ['Audit Log', 'Change Log'],
    tags: ['ログ', '監査'], iconKey: 'ShieldCheck' },
  { path: '/settings/ai-import-history', group: 'audit', labelJa: 'AI取り込み履歴', capability: CAP.AI_IMPORT_HISTORY,
    purpose: 'AIによる書類取り込みバッチの履歴',
    aliasesJa: ['AI Import History', 'Ingest History'],
    tags: ['ログ', 'AI', '書類'], iconKey: 'ClockCounterClockwise' },
  { path: '/settings/email-log',        group: 'audit', labelJa: 'メール送信履歴', capability: CAP.MANAGE_EMAIL,
    purpose: '送信されたメールの履歴',
    aliasesJa: ['Email Log', 'Sent Mail'],
    tags: ['ログ', 'メール'], iconKey: 'ClockClockwise' },
  { path: '/settings/changelog',        group: 'audit', labelJa: '更新履歴', capability: CAP.ALWAYS,
    purpose: 'Sensbey Web アプリの更新履歴',
    aliasesJa: ['Changelog', 'Release Notes'],
    tags: ['ログ', 'リリース'], iconKey: 'NewspaperClipping' },

  // ── システム管理 ─────────────────────────────────────────────────────
  { path: '/settings/data-audit', group: 'system', labelJa: 'データ点検', capability: CAP.IS_ADMIN,
    purpose: '仮データ・不整合の点検と、購入記録の点検（どちらもドライラン）',
    aliasesJa: ['Data Audit', '購入記録', 'データ点検', '仮データ', '番号の直し'],
    tags: ['システム', 'データ'], iconKey: 'MagnifyingGlass' },
  // 2026-10-21 · 「テストデータの整理」は本番運用開始につき削除（/settings/data-prep は設定へ戻す）

  // UX consolidation (2026-06): the code interpreter lives in 設定 (システム管理).
  // /ai/code は /settings/ai-code へリダイレクト。
  { path: '/settings/ai-code', group: 'system', labelJa: 'コードインタープリタ',
    capability: CAP.IS_ADMIN,
    purpose: 'ブラウザ内でPythonを実行してデータを確認・整形します',
    aliasesJa: ['Code Interpreter', 'Pyodide', 'コード実行'],
    tags: ['ツール', 'データ'],
    iconKey: 'Code' },
];

// Convenience helpers for consumers ──────────────────────────────────────
export function itemsForGroup(groupId) {
  return SETTINGS_REGISTRY.filter((r) => r.group === groupId && !r.deprecated);
}

export function isRegistered(path) {
  return SETTINGS_REGISTRY.some((r) => r.path === path);
}

// Case-insensitive multi-field search across the registry.
// Matches on labelJa, aliasesJa, tags, purpose, and (as a last resort) path.
// Returns unique paths in registry order.
export function searchRegistry(query, { includeDeprecated = false } = {}) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return [];
  const seen = new Set();
  const out = [];
  for (const r of SETTINGS_REGISTRY) {
    if (!includeDeprecated && r.deprecated) continue;
    const hay = [
      r.labelJa,
      r.purpose,
      r.path,
      ...(r.aliasesJa || []),
      ...(r.tags || []),
    ].map((s) => (s || '').toLowerCase()).join(' \u0001 ');
    if (hay.includes(q)) {
      const key = `${r.path}::${r.labelJa}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(r);
    }
  }
  return out;
}

// Retired-route redirect matrix (documentation + tests).
// Everything else stays where it is — this is intentionally short.
export const REDIRECT_MATRIX = [
  { from: '/ai/code', to: '/settings/ai-code',
    reason: 'コードインタープリタは設定（システム管理）に統合しました' },
];
